import { DndContext, DragEndEvent } from '@dnd-kit/core'
import { SortableContext } from '@dnd-kit/sortable'
import { useEffect, useState } from 'react'
import { useParams } from 'react-router-dom'
import { Button } from '../../../../components/button'
import { BackButton } from '../../../../components/button/backButton'
import { HomeItemCard } from '../../../../components/homePage/homeItemCard'
import { SectionContainer } from '../../../../components/homePage/sectionContainer'
import Icon from '../../../../components/icon'
import Dropdown from '../../../../components/inputs/dropdown'
import { AddHomeSectionModal } from '../../../../components/modal/app_modals/addHomeSectionModal'
import { SelectSectionContentModal } from '../../../../components/modal/app_modals/selectSectionContentModal'
import { LoadingOval } from '../../../../components/spinner/Spinner'
import { PageHeader } from '../../../../components/typography/PageHeader'
import { PRIMARY_COLOR } from '../../../../constants'
import {
  deepCopy,
  moveElement,
  removeElementAtIndex,
  stripSingleSectionFields,
} from '../../../../helpers'
import { useDebounceSearch } from '../../../../hooks/useDeboucedSearch'
import { useModalControl } from '../../../../hooks/useModalControl'
import Layout from '../../../../layout'
import {
  useGetHomePageSectionQuery,
  useUpdateHomeSectionMutation,
} from '../../../../redux-toolkits/home-page/homePage.slice'
import {
  ContentType,
  SectionItem,
  SectionSort,
  SingleSection,
} from '../../../../redux-toolkits/home-page/homePage.type'

export const sectionContentTitle: Record<ContentType, string> = {
  CATEGORIES: 'Categories',
  CREATORS: 'Creator',
  ESSENTIALS: 'Essentials',
  GOOD_LISTS: 'Good List',
  PRODUCTS: 'Products',
  MANUAL_ITEM: 'Explore',
  HOMEPAGE_BANNER: 'Banner',
}

export const sectionSortLabels: Record<SectionSort, string> = {
  newest: 'Newest',
  price_high: 'High price - Low price',
  price_low: 'Low price - High price',
}

const limits = [4, 8, 10, 12]

export const SingleSectionPage = () => {
  const [section, setSection] = useState<SingleSection>()

  const { id } = useParams<{ id: string }>()

  const { handleModalClose, handleModalOpen, setModalOpen, modal, modalOpen } =
    useModalControl()

  const { data: sectionData, isLoading: fetchingSectionData } =
    useGetHomePageSectionQuery(
      {
        id: id ?? '',
      },
      {
        refetchOnMountOrArgChange: true,
        skip: !id,
      },
    )

  const [modifySection, { isLoading: modifyingSection }] =
    useUpdateHomeSectionMutation()

  useDebounceSearch({
    searchQuery: section,
    onSearch: async () => {
      if (section && id) {
        const payload = stripSingleSectionFields(section)

        await modifySection({
          id: id,
          payload: {
            ...payload,
          },
        })
      }
    },
    delay: 2000,
  })

  const handleSectionDragEnd = (e: DragEndEvent, id: string) => {
    const { active, over } = e

    if (over && active.id !== over.id) {
      if (section) {
        const sectionCopy: SingleSection = deepCopy(section)

        const sectionItems: SectionItem[] = deepCopy(sectionCopy.items)

        const sectionItemsMod = sectionItems.map((it, idx) => {
          const item = it.type === 'MANUAL_ITEM' ? it.manualContent : it.item

          return {
            id: `${item?._id}-${idx}`,
            ...it,
          }
        })

        const oldIndex = sectionItemsMod.findIndex(
          (item) => item.id === active.id,
        )
        const newIndex = sectionItemsMod.findIndex(
          (item) => item.id === over.id,
        )

        const modifiedSection: SingleSection = {
          ...sectionCopy,
          items: moveElement(sectionItems, oldIndex, newIndex),
        }

        setSection(modifiedSection)
      }
    }
  }

  useEffect(() => {
    if (sectionData?.data) {
      setSection(sectionData.data)
    }
  }, [sectionData])

  return fetchingSectionData ? (
    <Layout>
      <div className="w-full items-center flex justify-center">
        {' '}
        <LoadingOval color={PRIMARY_COLOR} />
      </div>
    </Layout>
  ) : (
    <Layout>
      <div className="flex flex-col gap-10">
        <BackButton />

        <div className="flex w-full gap-4 flex-wrap justify-between">
          <div>
            <PageHeader
              title={`${section?.contentTypes
                ?.map((ct) => sectionContentTitle[ct])
                .join(' + ')} Section`}
              className="!mb-0"
            />

            <p className="text-black">{section?.items?.length ?? 0} added</p>
          </div>

          <div className="flex items-center gap-6">
            <Button
              type="button"
              label="Edit"
              onClick={() => {
                handleModalOpen('addNewSectionModal')
              }}
              primary={false}
              className="!w-fit"
            />

            <Button
              type="button"
              label={section?.status === 'PUBLISHED' ? 'Unpublish' : 'Publish'}
              onClick={async () => {
                if (section) {
                  const sectionCopy = deepCopy(section)

                  const payload = stripSingleSectionFields(sectionCopy)

                  await modifySection({
                    id: id ?? '',
                    payload: {
                      ...payload,
                      status:
                        section?.status === 'PUBLISHED'
                          ? 'ARCHIVED'
                          : 'PUBLISHED',
                    },
                  })
                }
              }}
              primary
              className="!w-fit"
              loading={modifyingSection}
            />
          </div>
        </div>

        <DndContext
          onDragEnd={(e) => handleSectionDragEnd(e, section?._id ?? '')}
          modifiers={[]}
          key={`${section?._id}`}
        >
          <SortableContext
            items={
              section?.items?.map((it, idx) => {
                const item =
                  it.type === 'MANUAL_ITEM' ? it.manualContent : it.item

                return {
                  id: `${item?._id}-${idx}`,
                  ...it,
                }
              }) ?? []
            }
          >
            <SectionContainer
              childrenClassName="!grid-cols-2 md:!grid-cols-4"
              sortable={false}
              id={`${section?._id}`}
              key={`${section?._id}`}
              title={section?.title}
              description={undefined}
              scrollable={false}
              showMoreOptions={false}
              section={section}
              headerExtra={
                section?.sectionType === 'AUTOMATED_CURATION' ? (
                  <div className="flex items-center gap-3">
                    <Dropdown
                      menuClassName=""
                      menuButton={
                        <Button
                          label={
                            section?.filters?.sort
                              ? `${sectionSortLabels[section.filters.sort]}`
                              : 'Choose sort method'
                          }
                          primary={false}
                          icon="filter"
                          className="!py-3 !px-4 !text-base !font-normal !w-full md:max-w-fit"
                        />
                      }
                      menuItems={Object.keys(sectionSortLabels).map((k) => {
                        return {
                          name: sectionSortLabels[k as SectionSort],
                          value: k,
                          onClick: async () => {
                            const sectionCopy: SingleSection = deepCopy(section)

                            const newSection = {
                              ...sectionCopy,
                              filters: {
                                ...sectionCopy.filters,
                                sort: k as unknown as SectionSort,
                              },
                            }

                            setSection(newSection)

                            const payload = stripSingleSectionFields(newSection)

                            await modifySection({
                              id: id ?? '',
                              payload: {
                                ...payload,
                              },
                            })
                          },
                        }
                      })}
                    />

                    <Dropdown
                      menuClassName=""
                      menuButton={
                        <Button
                          label={
                            section?.filters?.maxItems
                              ? `Show: ${section.filters.maxItems} items`
                              : 'Choose limit'
                          }
                          primary={false}
                          icon="filter"
                          className="!py-3 !px-4 !text-base !font-normal !w-full md:max-w-fit"
                        />
                      }
                      menuItems={limits.map((k) => {
                        return {
                          name: `${k}`,
                          value: k,
                          onClick: async () => {
                            const sectionCopy: SingleSection = deepCopy(section)

                            const newSection = {
                              ...sectionCopy,
                              filters: {
                                ...sectionCopy.filters,
                                maxItems: k as unknown as number,
                              },
                            }

                            setSection(newSection)

                            const payload = stripSingleSectionFields(newSection)

                            await modifySection({
                              id: id ?? '',
                              payload: {
                                ...payload,
                              },
                            })
                          },
                        }
                      })}
                    />
                  </div>
                ) : (
                  <button
                    className="w-fit text-black flex items-center gap-2"
                    type="button"
                    onClick={() => {
                      handleModalOpen('selectSectionContentModal')
                    }}
                  >
                    {(section?.items?.length ?? 0) > 0
                      ? 'Modify content'
                      : 'Add content'}
                    <Icon width={20} height={20} id="plus-circle" />
                  </button>
                )
              }
            >
              {section?.items?.map((it, idx) => {
                const item =
                  it.type === 'MANUAL_ITEM'
                    ? it.manualContent
                    : it.type === 'HOMEPAGE_BANNER'
                      ? it.homepageBanner
                      : it.item

                return (
                  <HomeItemCard
                    type={it.type}
                    item={item}
                    id={`${item?._id}-${idx}`}
                    key={`${item?._id}-${idx}`}
                    sortable={section.sectionType !== 'AUTOMATED_CURATION'}
                    className="border rounded-2xl p-4"
                    onRemove={
                      section.sectionType !== 'MANUAL_CURATION' &&
                      section.sectionType !== 'MARKETING_BANNER'
                        ? undefined
                        : async () => {
                            const sectionCopy: SingleSection = deepCopy(section)

                            const payload =
                              stripSingleSectionFields(sectionCopy)

                            await modifySection({
                              id: id ?? '',
                              payload: {
                                ...payload,
                                items: removeElementAtIndex(
                                  [...(payload.items ?? [])],
                                  idx,
                                ),
                              },
                            })
                          }
                    }
                  />
                )
              })}
            </SectionContainer>
          </SortableContext>
        </DndContext>
      </div>

      {section && (
        <AddHomeSectionModal
          open={modal === 'addNewSectionModal' && modalOpen}
          setOpen={setModalOpen}
          onCloseCallback={() => {
            handleModalClose()
          }}
          onSuccessCallback={() => {
            handleModalClose()
          }}
          mode="edit"
          section={section}
        />
      )}

      {section && id ? (
        <SelectSectionContentModal
          open={modal === 'selectSectionContentModal' && modalOpen}
          setOpen={setModalOpen}
          section={section}
          contentTypes={section.contentTypes}
          maxSelection={
            Object.fromEntries(
              section.contentTypes.map((key) => [key, 10]),
            ) as Record<ContentType, number>
          }
          onCloseCallback={() => {
            handleModalClose()
          }}
          onSuccessCallback={async (sectionItems) => {
            const sectionCopy: SingleSection = deepCopy(section)

            const sectionCopyMod: SingleSection = {
              ...sectionCopy,
              items: sectionItems,
            }

            const payload = stripSingleSectionFields(sectionCopyMod)

            const resp = await modifySection({
              id: id,
              payload: {
                ...payload,
              },
            })
            if (resp?.data?.success) {
              handleModalClose()
            }
          }}
          loading={modifyingSection}
        />
      ) : undefined}
    </Layout>
  )
}
